/**
 * Auth API — RTK Query endpoints injected into the shared `api` instance.
 *
 * Endpoints:
 *   - me            GET  /users/me/          — fetch authenticated user profile
 *   - login         POST /auth/login/        — email + password → HttpOnly cookie
 *   - signup        POST /auth/signup/       — create account + auto-login
 *   - logout        POST /auth/logout/       — invalidate server session / cookie
 *   - forgotPassword POST /auth/password/reset/         — send reset email (code by email)
 *   - setNewPassword POST /auth/password/reset/confirm/ — submit {email, code, new_password}
 *   - changePassword  POST /auth/password/change/ — change password for logged-in user
 *
 * The `User` type is imported directly from the sub-path so we can use the
 * name `User` without a re-export alias.
 */
import { api } from '@/services/api';
import type { User } from 'shared/api/types/user';

import { normalizeUser } from './normalizeUser';

// ---------------------------------------------------------------------------
// Request / response shapes
// ---------------------------------------------------------------------------

export interface LoginRequest {
  email: string;
  password: string;
}

export interface LoginResponse {
  /** Access token — present even when cookies are also set. */
  access?: string;
  /** Refresh token. */
  refresh?: string;
  /**
   * Inline user payload. The backend `LoginView` (extends SimpleJWT
   * `TokenObtainPairView`) returns ONLY `{access, refresh}` — `user` is
   * undefined here. LoginScreen consumes this by fetching `/users/me/`
   * after a successful login. Kept on the type so a future backend
   * extension that surfaces the user inline stays compatible.
   */
  user?: User;
}

export interface SignupRequest {
  email: string;
  first_name: string;
  last_name: string;
  password: string;
  /** Phone number with international prefix (e.g. '+5906123456'). */
  phone_number: string;
  /** Territory code (e.g. 'mtq'). Required by backend product rules. */
  territory_code: string;
  /** ISO-8601 date string (YYYY-MM-DD). Required by backend (min age 15). */
  date_of_birth: string;
  /** Must be true — user explicitly accepted the Terms of Service. */
  accepted_terms: true;
  /** Must be true — user explicitly accepted the Privacy Policy. */
  accepted_privacy: true;
  /** Optional opt-in to marketing emails. */
  accepted_marketing?: boolean;
}

export interface SignupResponse {
  user: User;
}

export interface ForgotPasswordRequest {
  email: string;
}

/**
 * Set-new-password request — matches the backend PasswordResetConfirmView
 * which validates the code + sets the new password in a single POST. There
 * is intentionally no separate "verify code" endpoint server-side; the
 * 6-digit code is carried over from the ResetCode screen via URL params
 * and validated atomically with the password update.
 */
export interface SetNewPasswordRequest {
  email: string;
  code: string;
  new_password: string;
}

/** Request shape for POST /auth/password/change/ (authenticated user). */
export interface ChangePasswordRequest {
  /** The user's current password. Backend field name: `old_password`. */
  old_password: string;
  /** The desired new password (min 10 chars enforced by schema, min 8 by backend). */
  new_password: string;
}

/**
 * Input shape for PATCH /users/me/ (A5 backend extension).
 *
 * Wire format is snake_case per the Django UserMeSerializer.
 * territory_id is the integer FK write field (territory is read-only nested).
 * language is aliased from preferred_language by the backend serializer.
 */
export interface UpdateMeInput {
  first_name?: string;
  last_name?: string;
  bio?: string;
  /** Avatar URL (already-uploaded DO Spaces URL, per A5 deviation). */
  avatar?: string | null;
  /** Integer FK to Territory model. Null clears the territory. */
  territory_id?: number | null;
  language?: 'fr' | 'en' | 'es' | 'pt';
  /** Free-form phone number (backend stores as E.164). */
  phone_number?: string;
  /** ISO YYYY-MM-DD. */
  date_of_birth?: string;
}

// ---------------------------------------------------------------------------
// Injected endpoints
// ---------------------------------------------------------------------------

export const authApi = api.injectEndpoints({
  endpoints: (build) => ({
    /**
     * Fetch the authenticated user's own profile.
     * Used by AuthGuard on mount and after login to hydrate the Redux auth slice.
     */
    me: build.query<User, void>({
      query: () => '/users/me/',
      transformResponse: (raw: Parameters<typeof normalizeUser>[0]) =>
        normalizeUser(raw),
      providesTags: [{ type: 'User', id: 'ME' }],
    }),

    /**
     * Email + password login.
     * On success the server sets an HttpOnly access + refresh cookie pair.
     */
    login: build.mutation<LoginResponse, LoginRequest>({
      query: (body) => ({
        url: '/auth/login/',
        method: 'POST',
        body,
      }),
      // The backend response is `{access, refresh}` — no `user` field —
      // so passing it through `normalizeUser` unconditionally throws
      // (`Cannot read 'id' of undefined`) and surfaces a generic
      // "Erreur de connexion" instead of letting LoginScreen navigate.
      // Only normalize when the backend actually returns the user inline;
      // LoginScreen calls `/users/me/` afterwards to hydrate the Redux
      // auth slice in the canonical case.
      transformResponse: (raw: Record<string, unknown>) => ({
        ...raw,
        user: raw.user
          ? normalizeUser(raw.user as Parameters<typeof normalizeUser>[0])
          : undefined,
      }) as LoginResponse,
      invalidatesTags: [{ type: 'User', id: 'ME' }],
    }),

    /**
     * Create a new account. Auto-logs in by setting the cookie.
     */
    signup: build.mutation<SignupResponse, SignupRequest>({
      query: (body) => ({
        url: '/auth/signup/',
        method: 'POST',
        body,
      }),
      transformResponse: (raw: { user: Parameters<typeof normalizeUser>[0] } & Record<string, unknown>) => ({
        ...raw,
        user: normalizeUser(raw.user),
      }) as SignupResponse,
      invalidatesTags: [{ type: 'User', id: 'ME' }],
    }),

    /**
     * Server-side logout — clears the HttpOnly cookie.
     * Redux state cleanup is handled by the component that calls this mutation.
     */
    logout: build.mutation<void, void>({
      query: () => ({
        url: '/auth/logout/',
        method: 'POST',
      }),
      invalidatesTags: ['User', 'Post', 'Notification'],
    }),

    /**
     * Trigger the password-reset email flow. Backend always returns 200 to
     * avoid leaking user existence (PasswordResetRequestView).
     */
    forgotPassword: build.mutation<void, ForgotPasswordRequest>({
      query: (body) => ({
        url: '/auth/password/reset/',
        method: 'POST',
        body,
      }),
    }),

    /**
     * Submit the new password atomically with the 6-digit code received by
     * email. The backend validates the code and sets the new password in a
     * single POST (PasswordResetConfirmView) — there is intentionally no
     * separate "verify code" endpoint server-side, the ResetCode screen
     * only checks the format (6 digits) and forwards the code via URL.
     */
    setNewPassword: build.mutation<void, SetNewPasswordRequest>({
      query: (body) => ({
        url: '/auth/password/reset/confirm/',
        method: 'POST',
        body,
      }),
    }),

    /**
     * Update the authenticated user's own profile.
     * PATCH /users/me/ — accepts snake_case fields per the Django serializer.
     * Invalidates the User ME cache so all components re-fetch the fresh profile.
     */
    updateMe: build.mutation<User, UpdateMeInput>({
      query: (body) => ({ url: '/users/me/', method: 'PATCH', body }),
      transformResponse: (raw: Parameters<typeof normalizeUser>[0]) =>
        normalizeUser(raw),
      invalidatesTags: [{ type: 'User', id: 'ME' }],
    }),

    /**
     * Change the authenticated user's password.
     * POST /auth/password/change/ — requires old_password + new_password.
     * Backend validates the old password and runs Django password validators.
     */
    changePassword: build.mutation<{ detail: string }, ChangePasswordRequest>({
      query: (body) => ({
        url: '/auth/password/change/',
        method: 'POST',
        body,
      }),
    }),
  }),
  overrideExisting: false,
});

export const {
  useMeQuery,
  useLazyMeQuery,
  useLoginMutation,
  useSignupMutation,
  useLogoutMutation,
  useForgotPasswordMutation,
  useSetNewPasswordMutation,
  useUpdateMeMutation,
  useChangePasswordMutation,
} = authApi;
